// @flow

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import pick from 'lodash/fp/pick';
import pickBy from 'lodash/fp/pickBy';

import type { FlashType, FlashTypes } from '../../../../components/Flash';
import type { SpecData, SpecResult } from '../../../../utils/vortex/types';
import type { Message } from '../../../../components/SpecInput';

import calculate, { processWarnings } from '../../../../utils/vortex/calculate';
import confirmAction from '../../../../utils/confirm-action';
import example from '../../../../utils/vortex/example';
import inputs, { defaultValues, isNumeric, validators } from '../../../../utils/vortex/inputs';
import renderSafeHTML from '../../../../utils/render-safe-html';
import trackEvent from '../track-event';

import Button, { SubmitButton, submitStyles } from '../../../../components/Button';
import Flash, { FlashMessage } from '../../../../components/Flash';
import Icon from '../../../../components/Icon';
import Page from '../../../../components/Page';
import SpecResultDisplay from '../../../../components/SpecResultDisplay';
import Specs from '../../../../components/Specs';
import SpecHeader from '../../../../components/SpecHeader';

const PRELOAD_EXAMPLE = false;
const REAL_TIME_UPDATES = true;

const defaultFlash: FlashType = {
  icon: 'clipboard',
  message: 'Enter specs below and tap <strong>CALCULATE</strong> to see recommended parts. Tap any label to reveal details about the input.',
  type: 'notice'
};

const ButtonToolbar = styled.div`
  padding: 2em 0 3em;
  text-align: center;
  button {
    margin: 0 0.25em;
  }
`;

const ContactLink = styled(Link)`
  ${submitStyles}
`;

const SpecSections = styled.div`
  ${props => window.Modernizr.flexbox && css`
    @media (min-width: 768px) {
      display: flex;
      flex-direction: row;
      padding: 0 1em;
      > section {
        width: 50%;
      }
    }
  `}
`;

const Assumptions = styled.div`
  background-color: ${({ theme }) => theme.lightBg1};
  background-image: linear-gradient(
    to bottom,
    ${props => props.theme.lightBg1},
    ${props => props.theme.lightBg2}
  );
  bottom: 0;
  left: 0;
  overflow-y: scroll;
  padding-bottom: 2em;
  position: fixed;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 4;
  > div {
    padding: 2em;
  }
  p {
    text-align: center;
  }
  hr {
    height: 1px;
    background: #ccc;
    margin: 1em 0;
    border: none;
  }
  label input {
    margin-right: 4px;
  }
`;

type Props = {};

type State = {|
  assumptionsDismissed: boolean,
  bushingKit?: string,
  displayAssumptions: boolean,
  flashes: Array<FlashType>,
  isCalculating: boolean,
  isValid: boolean,
  messages: { [key: ?string]: Message },
  result: ?SpecResult,
  specs: SpecData,
  warnings: { [key: string]: FlashType },
  willDismissAssumptions: boolean
|}

class Calculator extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const assumptionsDismissed = window.Modernizr.localstorage
      ? (parseInt(localStorage.getItem('assumptionsDismissed'), 10) === 1 ? true : false)
      : false;

    const state = {
      // Assumptions have been dismissed
      assumptionsDismissed: assumptionsDismissed,
      // Show the assumptions list or not
      displayAssumptions: false,
      // Flash notifications
      flashes: [defaultFlash],
      // Currently processing a calculation
      isCalculating: false,
      // Are current specs valid
      isValid: false,
      // Validation issues
      messages: {},
      // Current result
      result: null,
      // Current entered specs
      specs: defaultValues,
      // Calculation warnings
      warnings: {},
      // Assumptions will be dismissed
      willDismissAssumptions: true
    };
    // console.log('state', state);

    this.state = state;
  }

  componentDidMount = () => {
    if (PRELOAD_EXAMPLE) {
      this.setState({ specs: example });
    }
    // console.log('specs', this.state.specs);
  }

  componentDidUpdate = (prevProps: Props, prevState: State) => {
    if (prevState.specs !== this.state.specs) {
      this.validate();
    }

    if (prevState.result !== this.state.result) {
      this.calcWarnings();
    }
  }

  //--------------//
  // Manage specs //
  //--------------//

  clearAll = (event: SyntheticEvent<>) => {
    event.preventDefault();
    confirmAction('Clear existing data?', this.clearData);
  }

  clearData = () => {
    // console.log('clearData');
    this.setState({
      isCalculating: false,
      isValid: false,
      flashes: [defaultFlash],
      messages: {},
      result: null,
      specs: defaultValues,
      warnings: {}
    });
  }

  loadSampleData = () => {
    this.setState({ specs: example });

    trackEvent('Sample Loaded');
  }

  showExample = (event: SyntheticEvent<>) => {
    event.preventDefault();
    confirmAction('Load sample data? This will replace all entered data.', this.loadSampleData);
  }

  updateSpecs = (key: string, value: ?mixed) => {
    // console.log('updateSpecs', key, value);

    const specs = Object.assign({}, this.state.specs, { [key]: value });
    this.setState({ specs });
  }

  //--------------//
  // Calculations //
  //--------------//

  calculate = () => {
    this.setState({ isCalculating: true });

    const specs: SpecData = this.state.specs;
    const result: SpecResult = calculate(specs);

    // console.log('calculate', result);

    this.setState({
      isCalculating: false,
      result
    });

    const payload = pick(['smr', 'bushingKit', 'backstop'], result);
    trackEvent('Calculated', payload);
  }

  onSubmitHandler = (event: SyntheticEvent<>) => {
    event.preventDefault();
    this.calculate();
    window.scrollTo(0, 0);

    if (!this.state.assumptionsDismissed) {
      if (document.body) {
        document.body.style.overflow = 'hidden';
      }
      this.setState({ displayAssumptions: true });
    }
  }

  onRevealHandler = () => {
    trackEvent('Revealed');
  }

  onDismissHandler = () => {
    // console.log('onDismiss');
    this.setState({ result: null });
  }

  validateSpec = (key: string, value: ?mixed) => {
    // console.log('validateSpec', key, value);

    const validator = validators[key];
    let message;

    if (!value) {
      message = {
        text: 'is not set'
      };
    }
    else if (!isNumeric(value)) {
      message = {
        text: 'must be a number',
        type: 'error'
      };
    }
    else if (!validator.validate(value, this.state.specs)) {
      message = {
        text: validator.message(),
        type: validator.level || 'warning'
      };
    }

    return message;
  }

  validate = () => {
    const specs = this.state.specs;
    // console.log('validate', specs);

    const validations = Object.keys(specs).reduce((messages, spec) => {
      const message = this.validateSpec(spec, specs[spec]);
      if (message) {
        messages[spec] = message;
      }
      return messages;
    }, {});

    const messages = pickBy(message => message.type != null, validations);
    const errors = pickBy(message => message.type === 'error' || message.type == null, validations);

    // console.log('messages', messages);
    // console.log('errors', errors);

    this.setState({
      messages,
      isValid: Object.keys(errors).length === 0
    }, () => {
      if (REAL_TIME_UPDATES && this.state.result && this.state.isValid) {
        this.calculate();
      }
    });
  }

  calcWarnings = () => {
    // console.log('calcWarnings');

    const result = this.state.result;
    if (!result) {
      return;
    }

    const warnings = processWarnings(result);
    // I donno. I'd like to use Object.values, but flow just won't do it.
    const flashes = Object.keys(warnings).map(warning => warnings[warning]);

    // console.log('warnings', warnings);
    // console.log('flashes', flashes);

    this.setState({
      flashes,
      warnings
    });
  }

  onBushingKitHandler = (bushingKit: string) => {
    // console.log('onBushingKitHandler', bushingKit);
    this.setState({ bushingKit });
  }

  //----------------//
  // Flash messages //
  //----------------//

  addFlashMessage = (message: string, type: FlashTypes = 'notice') => {
    const flashes: Array<FlashType> = [...this.state.flashes];
    const flash: FlashType = { message, type };
    flashes.push(flash);
    this.setState({ flashes });
  }

  clearFlashMessages = () => {
    this.setState({ flashes: [] });
  }

  dismissFlash = (atIndex: number) => {
    const flashes = [...this.state.flashes];
    flashes.splice(atIndex, 1);
    this.setState({ flashes });
  }

  dismissAssumptions = () => {
    // console.log('dismissAssumptions', this.state.willDismissAssumptions);
    if (document.body) {
      document.body.style.overflow = '';
    }
    this.setState({ displayAssumptions: false });
    if (this.state.willDismissAssumptions && window.Modernizr.localstorage) {
      localStorage.setItem('assumptionsDismissed', '1');
    }
  }

  //--------//
  // Render //
  //--------//

  renderFlashes = () => {
    const flashes = this.state.flashes;
    if (flashes && flashes.length) {
      return (
        <Flash>
          {this.state.flashes.map((flash, index) => (
            <FlashMessage key={index} index={index} {...flash} dismiss={this.dismissFlash}>
              {renderSafeHTML(flash.message, 'span')}
            </FlashMessage>
          ))}
        </Flash>
      );
    }
  }

  renderSpecResult = () => {
    if (this.state.result) {
      return (
        <SpecResultDisplay
          result={this.state.result}
          onDismiss={this.onDismissHandler}
          onReveal={this.onRevealHandler}
          onBushingKit={this.onBushingKitHandler}
          isValid={this.state.isValid}
          warnings={this.state.warnings}
        />
      );
    }
  }

  submitDisabled = () => {
    if (this.state.isCalculating || !this.state.isValid) {
      return 'disabled';
    }
  }

  renderContactButton = () => {
    const result = this.state.result;
    if (result == null) {
      return;
    }

    const to = {
      pathname: '/contact',
      state: {}
    };
    if (this.state.bushingKit != null) {
      to.state.bushingKit = this.state.bushingKit;
    }

    to.state.result = result;
    to.state.specs = this.state.specs;

    // console.log(to);

    return (
      <ContactLink to={to}>
        <Icon type="chat" withMargin={true} />
        <span>Contact Sales</span>
      </ContactLink>
    );
  }

  assumptionsChanged = (event: SyntheticEvent<HTMLInputElement>) => {
    const willDismissAssumptions = event.target.checked ? 1 : 0;
    this.setState({
      willDismissAssumptions: Boolean(willDismissAssumptions)
    });
    // console.log('assumptionsChanged', willDismissAssumptions);
    if (window.Modernizr.localstorage) {
      localStorage.setItem('assumptionsDismissed', willDismissAssumptions.toString());
    }
  }

  renderAssumptions() {
    return (
      <Assumptions>
        <div>
          <SpecHeader>Calculator Assumptions</SpecHeader>
          <p><strong>Assumptions that need consideration when using this calculator include but may not be limited to:</strong></p>
          <ul>
            <li>It only calculates what gear box and gearbox components are required and does not consider any other components of your conveyor, or that your input is reasonable.</li>
            <li>This calculation assumes that conveyor is on rollers and that certain component weights and effects are negligible compared to conveyed materials.</li>
            <li>Only valid for Vortex gearboxes and Vortex components.</li>
            <li>You are responsible for your own double checking.</li>
          </ul>
          <hr />
          <p>
            <label>
              <input type="checkbox" checked={this.state.willDismissAssumptions} onChange={this.assumptionsChanged} /> Don't show this assumptions list again
            </label>
          </p>
        </div>
        <SubmitButton onClick={this.dismissAssumptions}>Show me the results</SubmitButton>
      </Assumptions>
    );
  }

  render() {
    const { displayAssumptions, result } = this.state;
    const formStyle = {
      paddingBottom: this.state.result ? '300px' : null
    };
    return (
      <Page title="Vortex Shaft Mount Calculator">
        {this.renderFlashes()}
        {this.renderSpecResult()}
        <form style={formStyle} onSubmit={this.onSubmitHandler}>
          <SpecSections>
            <Specs
              title="Conveyor Mechanical Specs"
              inputs={inputs.mechanical}
              data={this.state.specs}
              onChange={this.updateSpecs}
              messages={this.state.messages}
              focusFirst={true}
            />
            <Specs
              title="Conveyor Thermal Specs"
              inputs={inputs.thermal}
              data={this.state.specs}
              onChange={this.updateSpecs}
              messages={this.state.messages}
            />
          </SpecSections>
          <ButtonToolbar>
            <Button size="sm" onClick={this.clearAll}>Clear All</Button>
            <Button size="sm" onClick={this.showExample}>Load Example</Button>
          </ButtonToolbar>
          {!result && (
            <SubmitButton disabled={this.submitDisabled()}>
              <Icon type="calculator" withMargin={true} />
              <span>{this.state.isCalculating ? 'Calculating' : 'Calculate'}</span>
            </SubmitButton>
          )}
        </form>
        {result && this.renderContactButton()}
        {displayAssumptions && this.renderAssumptions()}
      </Page>
    );
  }

}

export default Calculator;



// WEBPACK FOOTER //
// ./src/views/tools/vortex/views/Calculator.js